import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ProviderCard from "../components/ProviderCard";
import {
  ShowerHead, Scissors, Stethoscope, GraduationCap, Home as HomeIcon, Building2,
  Footprints, Bird, Squirrel, HeartHandshake, MapPin, Search, QrCode, ShieldCheck,
  Bell, Wallet, ArrowRight, Heart,
} from "lucide-react";

const ICONS = { ShowerHead, Scissors, Stethoscope, GraduationCap, Home: HomeIcon, Building2, Footprints, Bird, Squirrel, HeartHandshake };

export default function Home() {
  const [meta, setMeta] = useState({ categories: [], cities: [], neighborhoods: [] });
  const [featured, setFeatured] = useState([]);
  const [partners, setPartners] = useState([]);
  const [lostPets, setLostPets] = useState([]);
  const [cat, setCat] = useState("");
  const [bairro, setBairro] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/providers").then((r) => setFeatured(r.data.filter((p) => p.featured).slice(0, 3)));
    api.get("/partners").then((r) => setPartners(r.data));
    api.get("/lost-pets").then((r) => setLostPets(r.data.slice(0, 3)));
  }, []);

  const search = () => {
    const params = new URLSearchParams();
    if (cat) params.set("categoria", cat);
    if (bairro) params.set("bairro", bairro);
    navigate(`/prestadores?${params.toString()}`);
  };

  return (
    <div>
      <section className="paw-pattern bg-brand-cream">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 bg-brand-mintLight text-brand-mintDark text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
              <MapPin className="w-3.5 h-3.5" /> Joinville — Santa Catarina
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-heading tracking-tight text-brand-graphite leading-tight">
              Tudo para o seu pet, <span className="text-brand-coral">num só lugar</span>
            </h1>
            <p className="text-lg text-muted-foreground mt-5 leading-relaxed max-w-lg">
              Encontre banho automático 24h, veterinários, passeadores e mais. Carteira digital do pet,
              tag inteligente com QR e uma comunidade que ajuda a trazer pets perdidos de volta.
            </p>
            <div className="bg-white rounded-2xl p-3 mt-8 shadow-[0_8px_30px_-6px_rgba(255,107,74,0.15)] flex flex-col sm:flex-row gap-2" data-testid="hero-search">
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="border-0 bg-brand-cream/60 sm:w-52" data-testid="hero-category-select">
                  <SelectValue placeholder="O que você precisa?" />
                </SelectTrigger>
                <SelectContent>
                  {meta.categories.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`hero-cat-${c.id}`}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={bairro} onValueChange={setBairro}>
                <SelectTrigger className="border-0 bg-brand-cream/60 sm:w-44" data-testid="hero-neighborhood-select">
                  <SelectValue placeholder="Bairro" />
                </SelectTrigger>
                <SelectContent>
                  {meta.neighborhoods.map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={search} className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-xl h-11 px-6" data-testid="hero-search-btn">
                <Search className="w-4 h-4 mr-2" /> Buscar
              </Button>
            </div>
          </div>
          <div className="relative hidden lg:block animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-brand-mustard rounded-3xl rotate-12 opacity-80" />
            <div className="absolute -bottom-8 -right-4 w-32 h-32 bg-brand-mint rounded-full opacity-70" />
            <img
              src="https://images.unsplash.com/photo-1647002380358-fc70ed2f04e0?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Cão feliz durante banho"
              className="relative rounded-[2rem] shadow-2xl object-cover w-full h-[440px]"
            />
            <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-md rounded-2xl px-4 py-3 shadow-lg flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-coral rounded-xl flex items-center justify-center">
                <ShowerHead className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-brand-graphite">Banho automático 24h</p>
                <p className="text-xs text-muted-foreground">a partir de R$ 69,90</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="categories-section">
        <h2 className="text-2xl sm:text-3xl font-bold font-heading text-brand-graphite">O que seu pet precisa hoje?</h2>
        <p className="text-muted-foreground mt-2">10 categorias de especialistas verificados em Joinville</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-8">
          {meta.categories.map((c, i) => {
            const Icon = ICONS[c.icon] || Heart;
            return (
              <Link key={c.id} to={`/prestadores?categoria=${c.id}`} data-testid={`category-card-${c.id}`}>
                <div className="bg-white rounded-2xl p-5 card-hover border border-brand-coral/5 text-center h-full animate-fade-up" style={{ animationDelay: `${i * 0.04}s` }}>
                  <div className={`w-12 h-12 mx-auto rounded-2xl flex items-center justify-center mb-3 ${i % 3 === 0 ? "bg-brand-coralLight text-brand-coral" : i % 3 === 1 ? "bg-brand-mintLight text-brand-mintDark" : "bg-brand-mustardLight text-brand-mustardDark"}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <p className="font-heading font-semibold text-sm text-brand-graphite">{c.name}</p>
                  {c.badge && <p className="text-[10px] text-brand-coral font-semibold mt-1 uppercase tracking-wide">{c.badge}</p>}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-10" data-testid="featured-section">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold font-heading text-brand-graphite">Destaques da semana</h2>
              <p className="text-muted-foreground mt-2">Prestadores verificados e bem avaliados pela comunidade</p>
            </div>
            <Link to="/prestadores" data-testid="see-all-providers">
              <Button variant="ghost" className="text-brand-coral">Ver todos <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {featured.map((p) => <ProviderCard key={p.provider_id} provider={p} categories={meta.categories} />)}
          </div>
        </section>
      )}

      <section className="bg-brand-graphite text-white mt-16">
        <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 bg-white/10 text-brand-mint text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
              <QrCode className="w-3.5 h-3.5" /> Tag inteligente PetMachine
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading leading-tight">Se seu pet se perder, quem encontrar te avisa na hora</h2>
            <p className="text-white/70 mt-4 leading-relaxed">
              A tag com QR code na coleira mostra os dados essenciais do seu pet e dispara um alerta para você assim que é escaneada. Sem app para quem encontra, sem complicação.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/loja" data-testid="home-cta-shop">
                <Button className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full px-6">Comprar minha tag</Button>
              </Link>
              <Link to="/pets-perdidos" data-testid="home-cta-lost">
                <Button variant="outline" className="rounded-full px-6 border-white/30 text-white hover:bg-white/10 bg-transparent">
                  <Heart className="w-4 h-4 mr-2 text-brand-coral" /> Ver pets desaparecidos
                </Button>
              </Link>
            </div>
            {lostPets.length > 0 && (
              <p className="text-sm text-brand-mustard mt-5">{lostPets.length} pet(s) procurando o caminho de casa agora em Joinville.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Wallet, title: "Carteira Digital", text: "Vacinas, vermífugo e lembretes automáticos" },
              { icon: Bell, title: "Alerta comunitário", text: "Rede de voluntários opt-in na sua região" },
              { icon: ShieldCheck, title: "Prestadores verificados", text: "Documentos conferidos pela nossa equipe" },
              { icon: QrCode, title: "QR na coleira", text: "Identificação instantânea e segura" },
            ].map((f, i) => (
              <div key={i} className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <f.icon className="w-6 h-6 text-brand-mint mb-3" />
                <p className="font-heading font-semibold">{f.title}</p>
                <p className="text-sm text-white/60 mt-1">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {partners.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16" data-testid="partners-section">
          <h2 className="text-2xl sm:text-3xl font-bold font-heading text-brand-graphite text-center">Nossos Parceiros</h2>
          <div className="grid sm:grid-cols-2 gap-6 mt-8 max-w-3xl mx-auto">
            {partners.map((p) => (
              <a key={p.partner_id} href={p.link} target="_blank" rel="noreferrer" data-testid={`partner-${p.partner_id}`}>
                <div className="bg-white rounded-2xl overflow-hidden card-hover border border-brand-coral/5">
                  <img src={p.banner_url} alt={p.name} className="w-full h-36 object-cover" />
                  <p className="font-heading font-semibold text-brand-graphite p-4">{p.name}</p>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-6 pb-8">
        <div className="bg-gradient-to-r from-brand-coral to-[#FF8A6B] rounded-[2rem] p-10 text-center text-white shadow-xl">
          <h2 className="text-2xl sm:text-3xl font-bold font-heading">É prestador de serviços pet?</h2>
          <p className="text-white/85 mt-3 max-w-xl mx-auto">Receba leads da sua região, ganhe o selo Verificado e apareça em destaque. Teste grátis por 7 dias.</p>
          <Link to="/cadastro?role=provider" data-testid="home-cta-provider">
            <Button className="bg-white text-brand-coral hover:bg-brand-cream rounded-full px-8 mt-6 font-semibold">Cadastrar meu negócio</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
